/**
 * Pagine redazionali con rotta fissa: slug → percorso canonico (§I.13,
 * decisione 196).
 * ---------------------------------------------------------------------------
 * ~17 pagine redazionali hanno DUE indirizzi validi: la rotta fissa nel
 * router e la rotta residuale `/p/<slug>/` (decisione 71) — contenuto
 * duplicato per un motore di ricerca. Il backend, deliberatamente, NON
 * dichiara quale sia il canonico: la corrispondenza slug→percorso vive nel
 * router della SPA, e una tabella nel backend sarebbe un secondo specchio,
 * cioè una seconda sorgente di divergenza.
 *
 * Questa mappa NON è uno specchio del router: È il router. `routes.tsx`
 * genera le sue rotte da qui, quindi aggiungere una riga significa creare la
 * rotta E dichiararne il canonico in un gesto solo — non possono divergere.
 *
 * Gli slug sono CONCORDATI col backend (decisione 41): la lista sta in
 * `backend/apps/core/importatori/pagine.py` (`PAGINE_LEGACY`) e nel
 * contratto (`docs/contratto-api-v2.md`).
 */

export const PAGINE_FISSE: Readonly<Record<string, string>> = {
  // ---------- Servizi ----------
  servizi: '/servizi/',
  'servizi-urp-digitale': '/servizi/urp-digitale/',
  'servizi-istruzione': '/servizi/istruzione/',
  'servizi-meteo': '/servizi/meteo/',

  // ---------- Territorio ----------
  'territorio-esplora': '/territorio/esplora/',
  'territorio-scopri': '/territorio/scopri/',
  'territorio-gusta': '/territorio/gusta/',

  // ---------- Rete civica ----------
  'rete-civica': '/rete-civica/',
  'rete-civica-cittadinanza-attiva': '/rete-civica/cittadinanza-attiva/',
  'rete-civica-attiva-comune': '/rete-civica/attiva-comune/',

  // ---------- Progetto ----------
  // NOTA per la redazione, non per il codice: il soggetto promotore è
  // attribuito in modo incoerente nelle statiche importate — da chiarire
  // in revisione, prima di pubblicare (l'avviso è in `note_redazione`).
  progetto: '/progetto/',
  'progetto-pilota': '/progetto/pilota/',
  'progetto-fonti-dati': '/progetto/fonti-dati/',
  'progetto-dati-aperti': '/progetto/dati-aperti/',

  // ---------- Legale (decisione 34) ----------
  'legale-privacy': '/legale/privacy/',
  'legale-cookie': '/legale/cookie/',
  // La bozza importata porta l'avviso "DA RISCRIVERE, NON REVISIONARE":
  // la dichiarazione statica si contraddice e va riscritta sullo stato
  // reale del nuovo portale prima di pubblicarla.
  'legale-accessibilita': '/legale/accessibilita/',
};

/**
 * Alias storici: percorso → slug. Rendono la stessa pagina della rotta fissa
 * ma NON sono il canonico — la pagina emette `rel="canonical"` verso la
 * rotta fissa. `/accessibilita/` resta una rotta viva (non un redirect):
 * è l'indirizzo breve convenzionale della dichiarazione di accessibilità,
 * ha i 301 legacy puntati addosso, e il canonical basta a togliere il
 * doppione dall'indice.
 */
export const ALIAS_PAGINE_FISSE: Readonly<Record<string, string>> = {
  '/accessibilita/': 'legale-accessibilita',
};

/**
 * Il percorso canonico di una pagina redazionale: la rotta fissa se esiste,
 * la rotta residuale `/p/<slug>/` (decisione 71) altrimenti.
 */
export function percorsoCanonicoPagina(slug: string): string {
  return PAGINE_FISSE[slug] ?? `/p/${slug}/`;
}
